/**
 * Claims out of a token this process holds.
 *
 * Decoded, never verified: the signature belongs to whoever issued the token,
 * and these values are only ever used to tell the operator what they hold.
 */
export function tokenClaims(accessToken: string): Record<string, unknown> | null {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;

    return JSON.parse(Buffer.from(payload, 'base64url').toString('utf-8'));
  } catch {
    return null;
  }
}

/** Scopes the identity provider actually granted, as opposed to those requested. */
export function grantedScopes(accessToken: string): string[] {
  const scope = tokenClaims(accessToken)?.scope;

  return typeof scope === 'string' ? scope.split(' ').filter(Boolean) : [];
}

/**
 * The services `sl login` asked for that came back with nothing.
 *
 * Auth0 grants the intersection of requested scopes and the caller's assigned
 * permissions by exact string match, and drops the rest without saying so. The
 * comparison is per service rather than per scope on purpose: the CLI requests
 * a superset of permission names (`atlas/*:*` and `atlas/*:read`), so most
 * individual requests are expected to be dropped and listing them would be
 * noise. A service with no scope at all is the condition that breaks commands.
 */
export function servicesWithoutScope(requestedScope: string, accessToken: string): string[] {
  const serviceOf = (scope: string): string | null => (scope.includes('/') ? scope.split('/')[0] : null);
  const granted = new Set(grantedScopes(accessToken).map(serviceOf).filter(Boolean));
  const requested = new Set(requestedScope.split(' ').map(serviceOf).filter(Boolean));

  return [...requested].filter((service) => !granted.has(service)).sort() as string[];
}
